import { Link } from "react-router-dom";
import { api, type Cycle, type LobbyCoverage, type MemberMoney } from "../api";
import { useAsync } from "../useAsync";
import { CommitteeSpend } from "./CommitteeSpend";

/** $1.2m, $64,000, $500 — enough precision to compare, not so much to count. */
function money(value: number): string {
  if (!value) return "$0";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}m`;
  if (value >= 10_000) return `$${Math.round(value / 1_000)}k`;
  return `$${Math.round(value).toLocaleString()}`;
}

function share(part: number, whole: number): string {
  if (!whole) return "—";
  const pct = (part / whole) * 100;
  return pct < 1 ? "<1%" : `${Math.round(pct)}%`;
}

/**
 * What a member was given.
 *
 * The section exists to hold two numbers next to each other, because either
 * one alone misleads. Lobbyists disclosing $14,000 sounds like a lot until it
 * sits beside the $65m the campaign raised; the $65m says nothing about who
 * wanted what. Neither is summed into the other and the layout is built so
 * that it cannot be done by accident.
 */
export function Money({ bioguide }: { bioguide: string }) {
  const found = useAsync(() => api.memberMoney(bioguide), [bioguide]);
  if (found.loading || found.error || !found.data) return null;

  const { lobbyist, campaign, coverage, party_committees: committees } = found.data;
  const latest = campaign[0];
  // Party committee money counts towards showing the section. For a member
  // whose seat the parties fought over it is far the largest thing here, and
  // a first-term member with no lobbyist gifts and no filed cycle yet would
  // otherwise have $17m of spending about them and no section to put it in.
  if (!lobbyist.gifts && !latest && committees.length === 0) return null;

  return (
    <section className="mt-12">
      <h2 className="font-display text-xl font-semibold">Who funds them</h2>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <FromLobbyists lobbyist={lobbyist} coverage={coverage} />
        <FromEveryone latest={latest} history={campaign} />
      </div>

      {lobbyist.donors.length > 0 && (
        <div className="mt-4 rounded-2xl border border-line bg-card p-6">
          <h3 className="text-sm font-semibold">Lobbying firms that reported giving</h3>
          {/* The organisation named on a row is not always the giver. Two
              thirds of these reports are an individual lobbyist disclosing
              their own money with their employer on the form, and read as
              "this company funded them" they describe something a company
              may not lawfully do. */}
          <WhoGaveIt byFiler={lobbyist.by_filer} />
          <ul className="mt-3 divide-y divide-line">
            {lobbyist.donors.map((donor) => (
              <li key={donor.registrant} className="flex items-baseline gap-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm" title={donor.registrant}>
                  {donor.registrant_id === null ? (
                    donor.registrant.toLowerCase()
                  ) : (
                    <Link
                      to={`/money/${donor.registrant_id}`}
                      className="hover:text-brand hover:underline"
                    >
                      {donor.registrant.toLowerCase()}
                    </Link>
                  )}
                </span>
                <span className="text-xs text-faint">
                  {donor.gifts === 1 ? "once" : `${donor.gifts}×`}
                </span>
                <span className="tnum w-20 text-right text-sm font-medium">
                  ${Math.round(donor.amount).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Last, and in its own component, because it is the one block here
          where the largest figure is usually money spent against the member
          rather than for them. */}
      <CommitteeSpend rows={committees} name={found.data.full_name ?? "this member"} />
    </section>
  );
}

/**
 * Which of the rows below are a firm's money and which are an employee's.
 *
 * Named on a row, "Apple Inc." reads as the giver. On the nine Apple rows
 * that started this, every one was an individual lobbyist's own cheque with
 * Apple on the form as their employer; Apple gave nothing and could not have.
 */
function WhoGaveIt({ byFiler }: { byFiler: MemberMoney["lobbyist"]["by_filer"] }) {
  // Absent entirely on a corpus predating the filer split. See FirmPage.
  const personal = byFiler?.lobbyist?.amount ?? 0;
  const filed = byFiler?.organization?.amount ?? 0;
  if (!personal) return null;
  return (
    <p className="mt-2 text-xs leading-relaxed text-muted">
      {filed > 0 ? `${money(personal)} of this` : "All of this"} was given by individual
      lobbyists out of their own pockets, with the firm named beside it only as their employer.
      Open a firm to see the split.
    </p>
  );
}

function FromLobbyists({
  lobbyist,
  coverage,
}: {
  lobbyist: MemberMoney["lobbyist"];
  coverage: LobbyCoverage;
}) {
  const missing = coverage.total ? coverage.unmatched / coverage.total : 0;
  return (
    <div className="rounded-2xl border border-line bg-card p-6">
      <h3 className="text-sm font-semibold">From registered lobbyists</h3>
      <p className="tnum mt-2 font-display text-3xl font-semibold">
        ${Math.round(lobbyist.total).toLocaleString()}
      </p>
      <p className="mt-1 text-sm text-muted">
        {lobbyist.gifts
          ? `${lobbyist.gifts} disclosed ${lobbyist.gifts === 1 ? "gift" : "gifts"} from ${
              lobbyist.donors_total
            } ${lobbyist.donors_total === 1 ? "firm" : "firms"}`
          : "nothing disclosed"}
      </p>

      {lobbyist.honorary > 0 && (
        <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
          A further{" "}
          <span className="font-medium text-ink">
            ${Math.round(lobbyist.honorary).toLocaleString()}
          </span>{" "}
          went on events held in their honour. That is money spent around them rather than given to
          them, so it is not counted above.
        </p>
      )}

      {/* The floor, stated. About a fifth of honoree names never resolve to
          anyone, so this total is "at least", and a page that implies
          otherwise is wrong in the direction that flatters. */}
      {missing > 0.02 && (
        <p className="mt-3 text-xs text-faint">
          At least. {Math.round(missing * 100)}% of disclosures name their recipient in a way that
          matches nobody in this corpus — often because the money went to a challenger or a
          governor, who are not members.
        </p>
      )}
    </div>
  );
}

function FromEveryone({ latest, history }: { latest?: Cycle; history: Cycle[] }) {
  if (!latest) {
    return (
      <div className="rounded-2xl border border-line bg-card p-6">
        <h3 className="text-sm font-semibold">From everyone</h3>
        <p className="mt-2 text-sm text-muted">No campaign filings on record for this member.</p>
      </div>
    );
  }
  const rows: [string, number][] = [
    ["from people", latest.individual],
    ["from PACs", latest.pac],
    ["from their party", latest.party],
    ["their own money", latest.from_self],
  ];
  return (
    <div className="rounded-2xl border border-line bg-card p-6">
      <h3 className="text-sm font-semibold">
        From everyone{" "}
        <span className="font-normal text-faint">· {latest.cycle} cycle</span>
      </h3>
      <p className="tnum mt-2 font-display text-3xl font-semibold">{money(latest.receipts)}</p>
      <p className="mt-1 text-sm text-muted">raised by their campaign</p>

      <ul className="mt-3 space-y-1.5 border-t border-line pt-3">
        {rows
          .filter(([, value]) => value > 0)
          .map(([label, value]) => (
            <li key={label} className="flex items-baseline gap-2 text-sm">
              <span className="flex-1 text-muted">{label}</span>
              <span className="tnum text-xs text-faint">{share(value, latest.receipts)}</span>
              <span className="tnum w-14 text-right font-medium">{money(value)}</span>
            </li>
          ))}
      </ul>

      {history.length > 1 && (
        <p className="mt-3 text-xs text-faint">
          {history.length} cycles on record, back to {history[history.length - 1].cycle}.
        </p>
      )}
    </div>
  );
}
